import { useSession } from './session';

/** True inside the Android/iOS app (Capacitor), false in a browser. */
export const isNativeApp = () => !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();

const CONFIGURED = (import.meta.env.VITE_API_URL as string | undefined) || '';
const BUILT_IN = CONFIGURED || (isNativeApp() ? '' : 'http://localhost:8787');
/** Production builds (web and app) carry their server; only builds without one let the user pick it. */
export const serverBuiltIn = () => !!CONFIGURED;

/** API base URL: the build's server, else the one set on the sign-in screen, else the local default. */
export function apiUrl(): string {
  return (CONFIGURED || localStorage.getItem('sd.api') || BUILT_IN).replace(/\/+$/, '');
}

export function setApiUrl(url: string) {
  localStorage.setItem('sd.api', url.trim().replace(/\/+$/, ''));
}

/** Check that an address really is a Sense Daily server. */
export async function pingServer(url: string): Promise<boolean> {
  try {
    const base = url.trim().replace(/\/+$/, '');
    for (const path of ['/auth/health', '/']) {
      const r = await fetch(base + path, { signal: AbortSignal.timeout(8000) }).catch(() => null);
      const d = await r?.json().catch(() => null);
      if (d?.service === 'sense-daily-api') return true;
    }
    return false;
  } catch {
    return false;
  }
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown; token?: string } = {}): Promise<T> {
  const token = init.token ?? useSession.getState().token;
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  if (!apiUrl()) throw new ApiError(0, 'no_server', 'Set the server address on the sign-in screen first.');
  let res: Response;
  try {
    res = await fetch(apiUrl() + path, { ...init, headers, body });
  } catch {
    throw new ApiError(0, 'offline', 'You are offline.');
  }
  // A body cut off mid-way (reload, weak signal) must not read as an empty success.
  const text = await res.text().catch(() => null);
  if (text === null) throw new ApiError(0, 'offline', 'The connection dropped. Try again.');
  let data: Record<string, unknown> & { error?: string; message?: string; issues?: unknown } = {};
  try { data = text ? JSON.parse(text) : {}; } catch { if (res.ok) throw new ApiError(0, 'bad_response', 'The server sent an unreadable reply. Try again.'); }
  if (!res.ok) {
    if (res.status === 401 && path.startsWith('/api/')) {
      // Session expired: try one silent refresh, then retry once.
      if (await useSession.getState().refresh()) return api<T>(path, { ...init, token: undefined });
      // Could not refresh (e.g. an employee's shift session ended): back to sign-in, data on the device is kept.
      if (useSession.getState().status === 'ready') {
        localStorage.removeItem('sd.session');
        useSession.setState({ status: 'signedOut', token: null });
      }
    }
    // Access turned off, expired or outside allowed hours: back to sign-in with the reason.
    if (res.status === 403 && typeof data.error === 'string' && data.error.startsWith('access_') && useSession.getState().status === 'ready') {
      sessionStorage.setItem('sd.signoutReason', data.message ?? 'Your access has changed.');
      localStorage.removeItem('sd.session');
      useSession.setState({ status: 'signedOut', token: null });
    }
    if (res.status === 401 && path.startsWith('/admin/') && useSession.getState().status === 'admin') {
      localStorage.removeItem('sd.session');
      useSession.setState({ status: 'signedOut', token: null });
    }
    // Validation errors carry one message per field; show them instead of a generic code.
    const issues = Array.isArray(data.issues) ? data.issues.map((i: { message?: string }) => i.message).filter(Boolean).join(' · ') : '';
    throw new ApiError(res.status, data.error ?? 'error', issues || data.message || data.error || `Request failed (${res.status})`);
  }
  return data as T;
}
